import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import ScrollToTop from "@/components/ScrollToTop";
import PublicLayout from "@/components/layouts/PublicLayout";
import AdminLayout from "@/components/layouts/AdminLayout";

import LandingPage from "@/pages/public/LandingPage";
import VehicleCatalogPage from "@/pages/public/VehicleCatalogPage";
import VehicleDetailPage from "@/pages/public/VehicleDetailPage";
import BookAppointmentPage from "@/pages/public/BookAppointmentPage";
import CancelAppointmentPage from "@/pages/public/CancelAppointmentPage";
import ModifyAppointmentPage from "@/pages/public/ModifyAppointmentPage";
import PromotionsPublicPage from "@/pages/public/PromotionsPublicPage";
import LoginPage from "@/pages/auth/LoginPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import ActivateAccountPage from "@/pages/auth/ActivateAccountPage";
import ForceChangePasswordPage from "@/pages/auth/ForceChangePasswordPage";
import SecurityEventsPage from "@/pages/admin/SecurityEventsPage";
import LegacyLeadsPage from "@/pages/admin/LegacyLeadsPage";
import DailyReportPage from "@/pages/admin/DailyReportPage";
import WebAnnouncementsPage from "@/pages/admin/WebAnnouncementsPage";
import PermissionsMatrixPage from "@/pages/admin/PermissionsMatrixPage";
import UsersPermissionsPage from "@/pages/admin/UsersPermissionsPage";
import AccesosPermisosPage from "@/pages/admin/AccesosPermisosPage";
import AbsencesCalendarPage from "@/pages/employee/AbsencesCalendarPage";

import DashboardPage from "@/pages/admin/DashboardPage";
import AppointmentsPage from "@/pages/admin/AppointmentsPage";
import AppointmentServicesPage from "@/pages/admin/AppointmentServicesPage";
import RentalCatalogsPage from "@/pages/admin/RentalCatalogsPage";
import CustomerProfilePage from "@/pages/admin/CustomerProfilePage";
import CustomerSearchPage from "@/pages/admin/CustomerSearchPage";
import VehiclesPage from "@/pages/admin/VehiclesPage";
import SellersPage from "@/pages/admin/SellersPage";
import EmployeesPage from "@/pages/admin/EmployeesPage";
import VacationsPage from "@/pages/admin/VacationsPage";
import HrPage from "@/pages/admin/HrPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import UsersPage from "@/pages/admin/UsersPage";
import UsersMigrationPage from "@/pages/admin/UsersMigrationPage";
import CatalogsSummaryPage from "@/pages/admin/catalogs/CatalogsSummaryPage";
import CatalogPage from "@/pages/admin/catalogs/CatalogPage";
import CentersPage from "@/pages/admin/CentersPage";
import WebContentPage from "@/pages/admin/WebContentPage";
import ExportPage from "@/pages/admin/ExportPage";
import OrgChartPage from "@/pages/admin/OrgChartPage";
import DataHealthPage from "@/pages/admin/DataHealthPage";
import PromotionsPage from "@/pages/admin/PromotionsPage";
import WhatsappDashboard from "@/pages/admin/WhatsappDashboard";
import CTAsAuditPage from "@/pages/admin/CTAsAuditPage";
import EmailConfigPage from "@/pages/admin/EmailConfigPage";
import ContactSubmissionsPage from "@/pages/admin/ContactSubmissionsPage";
import JobApplicationsPage from "@/pages/admin/JobApplicationsPage";
import GtmConfigPage from "@/pages/admin/GtmConfigPage";
import FormConfigPage from "@/pages/admin/FormConfigPage";
import ContentSyncPage from "@/pages/admin/ContentSyncPage";
import BackupsPage from "@/pages/admin/BackupsPage";
import ManagedBackupsPage from "@/pages/admin/ManagedBackupsPage";
import PermissionsDiagnosticPage from "@/pages/admin/PermissionsDiagnosticPage";
import PermissionsAuditPage from "@/pages/admin/PermissionsAuditPage";
import RrhhConfigPage from "@/pages/admin/RrhhConfigPage";
import CommunicationsHubPage from "@/pages/admin/CommunicationsHubPage";
import NotificationsPage from "@/pages/admin/NotificationsPage";
import VoucherDashboard from "@/pages/admin/vouchers/VoucherDashboard";
import VoucherList from "@/pages/admin/vouchers/VoucherList";
import VoucherIssue from "@/pages/admin/vouchers/VoucherIssue";
import VoucherTemplates from "@/pages/admin/vouchers/VoucherTemplates";
import VoucherRedeem from "@/pages/admin/vouchers/VoucherRedeem";
import VoucherDetail from "@/pages/admin/vouchers/VoucherDetail";
import VoucherReports from "@/pages/admin/vouchers/VoucherReports";
import VoucherConfig from "@/pages/admin/vouchers/VoucherConfig";
import VoucherTemplateEditor from "@/pages/admin/vouchers/VoucherTemplateEditor";
import VoucherValidation from "@/pages/VoucherValidation";
import AdvisorLookupPage from "@/pages/public/AdvisorLookupPage";
import PrivacyPolicyPage from "@/pages/public/PrivacyPolicyPage";
import LegalNoticePage from "@/pages/public/LegalNoticePage";
import CookiePolicyPage from "@/pages/public/CookiePolicyPage";
import MecanicaPage from "@/pages/public/MecanicaPage";
import ChapaPage from "@/pages/public/ChapaPage";
import RecambiosPage from "@/pages/public/RecambiosPage";
import ConocenosPage from "@/pages/public/ConocenosPage";
import ContactPage from "@/pages/public/ContactPage";
import TrabajaPage from "@/pages/public/TrabajaPage";

import EmployeeDashboardPage from "@/pages/employee/EmployeeDashboardPage";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Iter 205 (P0) — Bloqueo por must_change_password usa useLocation (react-router)
  // en vez de window.location.pathname para evitar mismatches durante navegación
  // en cascada (login → /admin → /cambiar-contrasena). Antes producía pantalla
  // en blanco porque el cambio entre rutas no era atómico con window.location.
  if (user.must_change_password && location.pathname !== "/cambiar-contrasena") {
    return <Navigate to="/cambiar-contrasena" replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/**
 * Portal Empleado: cualquier usuario autenticado con `employee_id` vinculado.
 * El rol NO importa: jefe_taller, admin, recepcion, rrhh, etc. también
 * pueden ser empleados (Enrique Sanz por ejemplo). Si no hay employee_id,
 * redirige al home.
 */
function EmployeePortalRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.employee_id) return <Navigate to="/" replace />;
  return children;
}

// Lista exhaustiva de roles que pueden tener acceso al panel admin.
// El acceso REAL a cada módulo se filtra después con RequireModule (per ruta)
// + sidebar (filtrado por permiso `ver`). Aquí solo decidimos "tiene chance"
// de entrar a /admin. El portal de empleado sigue con su lista propia.
const ADMIN_PANEL_ROLES = [
  "super_admin", "admin", "jefe_taller", "jefe_recambios",
  "recepcion", "rrhh", "comercial", "asesor",
];

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path="/vehiculos" element={<VehicleCatalogPage />} />
        <Route path="/vehiculos/:id" element={<VehicleDetailPage />} />
        <Route path="/cita" element={<BookAppointmentPage />} />
        <Route path="/cita/cancelar/:token" element={<CancelAppointmentPage />} />
        <Route path="/cita/modificar/:token" element={<ModifyAppointmentPage />} />
        <Route path="/contacta-asesor" element={<AdvisorLookupPage />} />
        <Route path="/mecanica" element={<MecanicaPage />} />
        <Route path="/chapa-y-pintura" element={<ChapaPage />} />
        <Route path="/recambios" element={<RecambiosPage />} />
        <Route path="/promociones-postventa" element={<PromotionsPublicPage />} />
        <Route path="/conocenos" element={<ConocenosPage />} />
        <Route path="/contacto" element={<ContactPage />} />
        <Route path="/trabaja-con-nosotros" element={<TrabajaPage />} />
        <Route path="/politica-de-privacidad" element={<PrivacyPolicyPage />} />
        <Route path="/aviso-legal" element={<LegalNoticePage />} />
        <Route path="/politica-de-cookies" element={<CookiePolicyPage />} />
      </Route>

      {/* Auth */}
      {/* Iter 205 (P0) — Si user.must_change_password, /login debe redirigir
          a /cambiar-contrasena y NO a /admin. Evita bucles de redirección. */}
      <Route path="/login" element={
        user
          ? (user.must_change_password
              ? <Navigate to="/cambiar-contrasena" replace />
              : <Navigate to={['employee', 'empleado'].includes(user.role) ? '/empleado' : '/admin'} replace />)
          : <LoginPage />
      } />
      <Route path="/validar/:code" element={<VoucherValidation />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/activar-cuenta" element={<ActivateAccountPage />} />
      <Route path="/cambiar-contrasena" element={
        <ProtectedRoute>
          <ForceChangePasswordPage />
        </ProtectedRoute>
      } />

      {/* Admin Panel */}
      <Route path="/admin" element={<ProtectedRoute roles={ADMIN_PANEL_ROLES}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="citas" element={<AppointmentsPage />} />
        <Route path="citas/servicios" element={<AppointmentServicesPage />} />
        <Route path="vehiculos-alquiler" element={<RentalCatalogsPage />} />
        <Route path="clientes" element={<CustomerSearchPage />} />
        <Route path="clientes/:customer_key" element={<CustomerProfilePage />} />
        <Route path="vehiculos" element={<VehiclesPage />} />
        <Route path="vendedores" element={<SellersPage />} />
        <Route path="empleados" element={<EmployeesPage />} />
        <Route path="vacaciones" element={<VacationsPage />} />
        <Route path="rrhh" element={<HrPage />} />
        <Route path="rrhh/configuracion" element={<RrhhConfigPage />} />
        <Route path="exportacion" element={<ExportPage />} />
        <Route path="organigrama" element={<OrgChartPage />} />
        <Route path="usuarios" element={<UsersPage />} />
        <Route path="usuarios/migracion" element={<UsersMigrationPage />} />
        <Route path="catalogos-rrhh" element={<CatalogsSummaryPage />} />
        <Route path="catalogos-rrhh/departamentos" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/puestos" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/categorias-profesionales" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/grupos-cotizacion" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/tipos-contrato" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/jornadas" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/situaciones-laborales" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/convenios" element={<CatalogPage />} />
        <Route path="catalogos-rrhh/centros-coste" element={<CatalogPage />} />
        <Route path="centros" element={<CentersPage />} />
        <Route path="contenido-web" element={<WebContentPage />} />
        <Route path="promociones" element={<PromotionsPage />} />
        <Route path="plantillas-email" element={<Navigate to="/admin/comunicaciones?tab=emails" replace />} />
        <Route path="configuracion" element={<SettingsPage />} />
        <Route path="salud-datos" element={<DataHealthPage />} />
        <Route path="whatsapp" element={<Navigate to="/admin/comunicaciones?tab=whatsapp" replace />} />
        <Route path="ctas-enlaces" element={<CTAsAuditPage />} />
        <Route path="emails-sistema" element={<Navigate to="/admin/comunicaciones?tab=emails" replace />} />
        {/* Iter 207 — Avisos web públicos */}
        <Route path="comunicaciones/avisos-web" element={<WebAnnouncementsPage />} />
        <Route path="email-config" element={<Navigate to="/admin/comunicaciones?tab=remitentes" replace />} />
        <Route path="consultas" element={<ContactSubmissionsPage />} />
        <Route path="candidaturas" element={<JobApplicationsPage />} />
        <Route path="gtm" element={<GtmConfigPage />} />
        <Route path="formularios" element={<Navigate to="/admin/comunicaciones?tab=formularios" replace />} />
        <Route path="comunicaciones" element={<CommunicationsHubPage />} />
        <Route path="notificaciones" element={<NotificationsPage />} />
        <Route path="seguridad/eventos" element={<SecurityEventsPage />} />
        <Route path="informes-automaticos" element={<DailyReportPage />} />
        <Route path="permisos-y-roles" element={<Navigate to="/admin/accesos-permisos?tab=matriz" replace />} />
        <Route path="usuarios-permisos" element={<Navigate to="/admin/accesos-permisos?tab=usuarios" replace />} />
        <Route path="accesos-permisos" element={<AccesosPermisosPage />} />
        <Route path="planificacion-ausencias" element={<AbsencesCalendarPage admin />} />
        <Route path="leads-historicos" element={<LegacyLeadsPage />} />
        <Route path="comunicaciones/plantillas-email" element={<EmailConfigPage />} />
        <Route path="sincronizar-contenido" element={<ContentSyncPage />} />
        <Route path="copias-seguridad" element={<ManagedBackupsPage />} />
        <Route path="copias-seguridad-legacy" element={<BackupsPage />} />
        <Route path="diagnostico-permisos" element={<PermissionsDiagnosticPage />} />
        <Route path="auditoria-permisos" element={<PermissionsAuditPage />} />
        <Route path="atenciones" element={<VoucherDashboard />} />
        <Route path="atenciones/bonos" element={<VoucherList />} />
        <Route path="atenciones/emitir" element={<VoucherIssue />} />
        <Route path="atenciones/plantillas" element={<VoucherTemplates />} />
        <Route path="atenciones/plantillas/editor/:id" element={<VoucherTemplateEditor />} />
        <Route path="atenciones/canjes" element={<VoucherRedeem />} />
        <Route path="atenciones/detalle/:id" element={<VoucherDetail />} />
        <Route path="atenciones/informes" element={<VoucherReports />} />
        <Route path="atenciones/config" element={<VoucherConfig />} />
      </Route>

      {/* Employee Portal: acceso por employee_id, NO por rol */}
      <Route path="/empleado" element={<EmployeePortalRoute><AdminLayout isEmployee /></EmployeePortalRoute>}>
        <Route index element={<EmployeeDashboardPage />} />
        <Route path="vacaciones" element={<VacationsPage />} />
        <Route path="calendario" element={<AbsencesCalendarPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
